import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Icon, Input, Skeleton, Stack } from "@chakra-ui/react";
import { MdHighlightOff, MdCheckCircleOutline } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import QrScanner from "qr-scanner";

import { PageContent, PageHeader, PageLayout } from "@/components/page";
import { EmptyState } from "@/components/ui/empty-state";
import { useNursery, useValidateTank } from "@/hooks";
import { Alert } from "@/components/ui/alert";
import { ITank } from "@/types/data";

type RouteParams = {
  nurseryId: string
}

function TankSearch() {
  const navigate = useNavigate();
  const { nurseryId } = useParams<RouteParams>();
  const { data, isPending, error } = useNursery(nurseryId!);
  const {
    validateTank,
    error: validateError,
    success: validateSuccess
  } = useValidateTank(nurseryId!);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [ tankId, setTankId ] = useState<string>("");

  const handleValidationResult = useCallback((tank?: ITank | void) => {
    if (tank) {
      setTimeout(() => navigate(`/nurseries/${nurseryId}/tanks/${tank.id}/`), 1000);
    }
  }, [navigate, nurseryId]);

  const handleInputBlur = () => validateTank(nurseryId!, tankId).then(handleValidationResult);

  useEffect(() => {
    let qrScanner: QrScanner;

    const startScanner = () => {
      if (qrScanner) {
        qrScanner.start().catch(() => startScanner());
      }
    };

    if (videoRef.current) {
      // Initialise the QR code scanner
      qrScanner = new QrScanner(
        videoRef.current,
        result => {
          setTankId(result);
          validateTank(nurseryId!, result).then(handleValidationResult);
          qrScanner.stop();
        },
      );

      // Start scanning
      startScanner();
    }

    return () => {
      // Stop scanning on unmount
      if (qrScanner) {
        qrScanner.stop();
      }
    };
  }, [handleValidationResult, navigate, nurseryId, validateTank]);

  return (
    <PageLayout>
      <PageHeader backLink={`/nurseries/${nurseryId}`}>Scan Tank QR Code</PageHeader>
      <PageContent>
        {error && (
          <EmptyState
            icon={<MdHighlightOff />}
            title="Error"
            description={error.message}
          />
        )}
        {isPending && (
          <Stack gap="4" alignItems="center" mt={8}>
            <Skeleton loading={isPending} width="16rem" height="16rem" />
            <Skeleton loading={isPending} w="full" />
          </Stack>
        )}
        {data && (
          <Stack gap="4" mt={8}>
            <Box textAlign="center">
              {validateSuccess ? (
                <Icon fontSize="200px">
                  <MdCheckCircleOutline />
                </Icon>
              ) : (
                <Box
                  as="video"
                  ref={videoRef}
                  w="60"
                  h="60"
                  mx="auto"
                  borderRadius="lg"
                  borderWidth="2px"
                  position="relative"
                  objectFit="cover"
                />
              )}
            </Box>
            <Input
              aria-label="Enter tank ID"
              textAlign="center"
              value={tankId}
              onChange={(e) => setTankId(e.target.value)}
              onBlur={handleInputBlur}
            />
            {validateError && <Alert status="error" title={validateError.message} />}
          </Stack>)}
      </PageContent>
    </PageLayout>
  );
}

export default TankSearch;
